import type {PropertyRule, PropertyTypeRule} from "../types";

/** Stable keys — `rule_id` / pivot `id` come from GET property or property-type template. */
export const HOUSEHOLD_PROPERTY_RULE_KEYS = [
  "occupancy_owner_sees_tenant_data",
  "occupancy_quota_resets_on_change",
  "occupancy_owner_may_act_on_rented_unit",
  "occupancy_owner_receives_copies",
  "occupancy_quota_abuse_auto_restrict",
] as const;

export type HouseholdPropertyRuleKey = (typeof HOUSEHOLD_PROPERTY_RULE_KEYS)[number];

export const OCCUPANCY_OWNER_SEES_TENANT_DATA_OPTIONS = ["None", "Aggregate", "Full"] as const;

export const OCCUPANCY_OWNER_RECEIVES_COPIES_OPTIONS = [
  "Never",
  "On own actions",
  "Always",
] as const;

type HouseholdCatalogEntry = {
  key: HouseholdPropertyRuleKey;
  name: string;
  type: "select" | "boolean";
  options?: readonly string[];
  defaultValue: string | boolean;
};

/** Labels/options when rules are absent from `GET /property/create` → `property_types[].rules`. */
export const HOUSEHOLD_PROPERTY_RULE_CATALOG: HouseholdCatalogEntry[] = [
  {
    key: "occupancy_owner_sees_tenant_data",
    name: "Owner sees tenant data",
    type: "select",
    options: OCCUPANCY_OWNER_SEES_TENANT_DATA_OPTIONS,
    defaultValue: "Aggregate",
  },
  {
    key: "occupancy_quota_resets_on_change",
    name: "Safelisting quota resets when occupancy changes",
    type: "boolean",
    defaultValue: false,
  },
  {
    key: "occupancy_owner_may_act_on_rented_unit",
    name: "Owner may act on a rented unit",
    type: "boolean",
    defaultValue: true,
  },
  {
    key: "occupancy_owner_receives_copies",
    name: "Owner receives copies of unit notifications",
    type: "select",
    options: OCCUPANCY_OWNER_RECEIVES_COPIES_OPTIONS,
    defaultValue: "On own actions",
  },
  {
    key: "occupancy_quota_abuse_auto_restrict",
    name: "Auto-restrict safelisting on suspected quota abuse",
    type: "boolean",
    defaultValue: false,
  },
];

export const HOUSEHOLD_PROPERTY_RULE_DEFAULTS: Record<
  HouseholdPropertyRuleKey,
  string | boolean
> = Object.fromEntries(
  HOUSEHOLD_PROPERTY_RULE_CATALOG.map((entry) => [entry.key, entry.defaultValue]),
) as Record<HouseholdPropertyRuleKey, string | boolean>;

/** Same 0/1/`true`/`false` coercion as the Households configure toggle. */
export function parseHouseholdBooleanRuleValue(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  return fallback;
}

function findPropertyRuleByKey(
  propertyRules: PropertyRule[],
  key: string,
): PropertyRule | undefined {
  return propertyRules.find((rule) => rule.key === key);
}

function findTypeRuleByKey(typeRules: PropertyTypeRule[], key: string): PropertyTypeRule | undefined {
  return typeRules.find((rule) => rule.key === key && rule.feature === "households");
}

/**
 * Merges the static household catalog with property-type template rules and saved property overrides.
 * Property override wins; then type template; then catalog default.
 */
export function mergeHouseholdPropertyRules(
  typeRules: PropertyTypeRule[],
  propertyRules: PropertyRule[],
): PropertyTypeRule[] {
  return HOUSEHOLD_PROPERTY_RULE_CATALOG.map((catalog) => {
    const typeRule = findTypeRuleByKey(typeRules, catalog.key);
    const propertyRule = findPropertyRuleByKey(propertyRules, catalog.key);
    const rule_id = propertyRule?.id ?? typeRule?.rule_id ?? 0;
    const value =
      propertyRule?.value !== undefined && propertyRule?.value !== null
        ? propertyRule.value
        : typeRule?.value !== undefined && typeRule?.value !== null
          ? typeRule.value
          : catalog.defaultValue;

    if (catalog.type === "select") {
      return {
        rule_id,
        feature: "households",
        key: catalog.key,
        name: typeRule?.name ?? propertyRule?.name ?? catalog.name,
        status: 1 as const,
        type: "select" as const,
        options: [...(catalog.options ?? [])],
        value: typeof value === "string" || typeof value === "number" ? String(value) : catalog.defaultValue,
      };
    }

    const boolValue = parseHouseholdBooleanRuleValue(value, Boolean(catalog.defaultValue));

    return {
      rule_id,
      feature: "households",
      key: catalog.key,
      name: typeRule?.name ?? propertyRule?.name ?? catalog.name,
      status: 1 as const,
      type: "boolean" as const,
      options: undefined as never,
      value: boolValue,
    };
  });
}

export function isHouseholdPropertyRuleKey(key: string): key is HouseholdPropertyRuleKey {
  return (HOUSEHOLD_PROPERTY_RULE_KEYS as readonly string[]).includes(key);
}

export function resolveHouseholdSelectOptions(key: string): string[] | undefined {
  const entry = HOUSEHOLD_PROPERTY_RULE_CATALOG.find((item) => item.key === key);
  if (!entry || entry.type !== "select" || !entry.options?.length) return undefined;
  return [...entry.options];
}
